// TODO: cleanup with research_assistant_gen_3 ff
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import {
  useCohortDefinitionStore,
  writeCohortProcessPendingMarker,
} from '@verantos-dev/app-state-cohort-definition';
import {
  avKeys,
  type AnalyticalVariableListItem,
} from '@verantos-dev/feature-analytical-variables';
import { conceptGroupKeys } from '@verantos-dev/feature-concept-group-detail';
import type { ChatComposerScopeChip } from '@verantos-dev/feature-epi-chat';
import {
  useEpiChatPage,
  type ResourceInvalidationResolvers,
  type UseEpiChatPageResult,
} from '@verantos-dev/feature-epi-chat';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';
import type { ListResponse } from '@verantos-dev/types-common';

import { toWireScope } from '../hooks/toWireScope';
import { useChatScopeBindings } from '../hooks/useChatScopeBindings';
import { projectPageKeys } from '../lib/api/queryKeys';

/**
 * Build the AV-side resolver bundle injected into the chat stream.
 * Lives at the project-page boundary because that's the first call
 * site that can freely import both `feature-epi-chat` AND
 * `feature-analytical-variables` without closing a build cycle.
 *
 * Built inside the React tree (vs. module-scope const) so the
 * optimistic-patch resolver has access to ``queryClient`` for the
 * synthetic library-item ``setQueryData`` write — without that, the
 * criteria card stays in the unresolved stub state for the duration
 * of the library refetch (which can take 1-2s+ when Snowflake is
 * slow). Memoized on the client; stable identity per ActiveChatProvider
 * mount.
 */
const useProjectPageResourceResolvers = (): ResourceInvalidationResolvers => {
  const queryClient = useQueryClient();
  return useMemo<ResourceInvalidationResolvers>(
    () => ({
      analyticalVariable: (analyticalVariableId, ctx) => {
        // QueryKey-typed so the union of read-only tuple shapes from the
        // factory functions doesn't narrow `keys` to a single tuple type
        // that rejects `push` of a different shape. The project artifacts
        // manifest is also invalidated so the project list page reflects
        // the new AV immediately (without this, the manifest cache stales
        // only on window focus).
        const keys: QueryKey[] = [
          avKeys.libraryDetail(analyticalVariableId),
          avKeys.detail(analyticalVariableId),
          projectPageKeys.artifactsForProject(ctx.projectId),
        ];
        // Flag on, the bare library prefix is not invalidated, so one update
        // doesn't refetch every mounted card; the stream handler refreshes the
        // library list.
        // TODO: cleanup with scoped_av_card_invalidation ff
        if (!ctx.scopedAvInvalidation) {
          keys.push(avKeys.library());
        }
        const cohortVersionId = ctx.parentIds?.cohortVersionId;
        if (cohortVersionId) {
          keys.push(avKeys.list(cohortVersionId));
        }
        return keys;
      },
      // The cohort detail page's criteria card reads its array off
      // ``avKeys.list(cohortVersionId)`` (`useCohortDefinitionQuery`),
      // which lives in `feature-analytical-variables` — out of reach
      // for `feature-epi-chat`'s inline cohort resolver. Inject it here
      // so cohort-mutating emits refresh the criteria immediately
      // instead of stranding the user on stale "Resolving to an
      // analytical variable…" placeholders until they hard-refresh.
      cohortVersionAvList: (cohortVersionId) => [avKeys.list(cohortVersionId)],
      // OR-6147: refetch the cohort profile (version-agnostic ``avKeys.profiles()``
      // prefix) so the builder learns when the agent's edit promoted the cohort to a
      // new server version. The definition query pins to
      // ``profile.latestCohortVersionId``, which only advances on a profile refetch;
      // without this, a version-advancing agent edit (any edit while the cohort isn't
      // already an UNSAVED draft) doesn't live-render until a manual refresh.
      cohortProfile: () => [avKeys.profiles()],
      // ``add_analytical_variable_to_cohort`` emits with an
      // ``av_attached`` patch — the project AV library list also
      // needs refetching so ``useCriteriaSection`` resolves
      // ``sourceLibraryAvId`` against the just-attached AV's
      // canonical row once it lands. ``setQueryData`` below
      // pre-populates the cache with a synthetic entry so the
      // criteria card resolves IMMEDIATELY (the canonical refetch
      // overwrites the synthetic entry when it returns).
      analyticalVariableLibrary: () => [avKeys.library()],
      // Invalidate the project artifact manifest + activity feed when
      // the RA stream reports a cohort or concept-group change. Injected
      // here because ``feature-epi-chat`` cannot import
      // ``feature-project-page`` without closing a build cycle.
      cohortProjectPageCaches: (projectId) => [
        projectPageKeys.artifactsForProject(projectId),
        projectPageKeys.activityForProject(projectId),
      ],
      conceptGroupProjectPageCaches: (projectId) => [
        projectPageKeys.artifactsForProject(projectId),
        projectPageKeys.activityForProject(projectId),
      ],
      // An RA analysis create / edit / process changes the project artifact
      // list row (name, status), so refetch the manifest + activity feed.
      analysisProjectPageCaches: (projectId) => [
        projectPageKeys.artifactsForProject(projectId),
        projectPageKeys.activityForProject(projectId),
      ],
      // OR-6461: the outer concept-group wrapper page's own
      // ``useConceptGroupDetail`` query (`feature-concept-group-detail`) is
      // out of reach for `feature-epi-chat`'s inline concept_group resolver,
      // same build-cycle reason as the AV resolvers above. Without this, an
      // agent draft edit invalidates the preview-scoped Definition/Concepts/
      // Mapping tab caches but leaves the wrapper page's own detail query
      // stale.
      conceptGroupDetail: (conceptGroupId) => [conceptGroupKeys.detail(conceptGroupId)],
      // OR-6147: an agent cohort mutation just landed. Flag the store so the
      // explicit-save hydration hook re-seeds from the server refetch even over
      // local staged edits — covers mutations the optimistic patch doesn't model
      // (e.g. setting an index date, which adds a new criterion rather than
      // swapping a stub). Same posture as applyCohortOptimisticPatch below.
      markCohortServerChange: () => {
        useCohortDefinitionStore.getState().setAgentRefetchPending(true);
      },
      applyCohortOptimisticPatch: (patch) => {
        // The store is reset on cohort-route param change
        // (``useCohortDefinitionLifecycle``), so a patch arriving after
        // navigation away naturally no-ops via the bounds checks below. No
        // ``hasStagedChanges`` guard — the user explicitly asked the agent to
        // mutate the cohort; their local staged edits are intentionally
        // overwritten by the agent's server-confirmed change (matches the
        // existing socket-driven hydration posture).
        const state = useCohortDefinitionStore.getState();

        // ``criterion_removed`` (OR-6074): drop the removed criteria by
        // position. Out-of-range indices are ignored (store reset on nav).
        // No library-cache synthesis — nothing new was bound.
        if (patch.kind === 'criterion_removed') {
          const removed = new Set(patch.removedIndices);
          useCohortDefinitionStore.setState({
            criteria: state.criteria.filter((_, i) => !removed.has(i)),
          });
          return;
        }

        // 1) Swap the bound criterion into the Zustand criteria array.
        //    ``av_attached`` (OR-6089) targets a stub by ``stubIndex``;
        //    ``av_replaced`` (OR-6073) targets an existing criterion by
        //    ``criterionIndex``. Both swap a single criterion in place.
        const swapIndex = patch.kind === 'av_attached' ? patch.stubIndex : patch.criterionIndex;
        if (swapIndex >= 0 && swapIndex < state.criteria.length) {
          const nextCriteria = [...state.criteria];
          // Cast: the wire schema validates ``newCriterion`` only as
          // an object record (intentional — the backend ships the
          // canonical Pydantic dump and the FE's criterion union is
          // shaped to match).
          nextCriteria[swapIndex] = patch.newCriterion as (typeof state.criteria)[number];
          useCohortDefinitionStore.setState({ criteria: nextCriteria });
        }

        // 2) Synthesize a library-list cache entry so
        //    ``useCriteriaSection``'s ``libraryById.get(sourceLibraryAvId)``
        //    hits immediately. The canonical library refetch
        //    (triggered by the ``analyticalVariableLibrary``
        //    invalidate alongside this call) eventually overwrites
        //    the synthetic entry with the full row; the fields we
        //    inject are the only ones the criteria card reads
        //    (kind / name / description / jsonValue branch).
        if (!patch.avDisplay) return;
        const display = patch.avDisplay;
        const syntheticItem: AnalyticalVariableListItem = {
          analyticalVariableId: display.analyticalVariableId,
          name: display.dataElement || display.name,
          dataElement: display.dataElement ?? null,
          description: display.description,
          format: '',
          jsonValue: null,
          sqlBody: null,
          kind: display.kind,
          owner: '',
          createdBy: { userId: '', transactionDate: '' },
        };
        // ``useAnalyticalVariableListQuery`` keys vary by
        // ``searchParams`` + ``projectId`` — write to every active
        // library-list cache slot via ``setQueriesData`` with the
        // ``avKeys.library()`` prefix.
        queryClient.setQueriesData<ListResponse<AnalyticalVariableListItem> | undefined>(
          { queryKey: avKeys.library() },
          (cached) => {
            // ``avKeys.library()`` is a PREFIX match, and a query under it can
            // fall back to its raw (un-parsed) response on a zod mismatch, whose
            // shape isn't ``ListResponse`` — so ``cached.data`` is not always an
            // array. Guard it: leave a malformed/empty slot untouched rather than
            // throwing. A throw here previously aborted the RESOURCE_UPDATED
            // handler before its invalidation ran, stranding the criteria card
            // until a manual refresh. (OR-6177)
            if (!cached || !Array.isArray(cached.data)) return cached;
            const items = cached.data;
            if (items.some((row) => row.analyticalVariableId === display.analyticalVariableId)) {
              return cached;
            }
            return { ...cached, data: [...items, syntheticItem] };
          }
        );
      },
      // OR-6158: agent triggered a cohort run. Flip the store into PROCESSING +
      // arm the execution gate so the detail header updates and the lifecycle's
      // 5s status poll starts; the poll carries it to PROCESSED on completion.
      onCohortProcessingStarted: () => {
        const store = useCohortDefinitionStore.getState();
        // TODO: cleanup with skip_process_confirmation_modal ff — breadcrumb so
        // a refresh before the backend commits PROCESSING can recover the gate.
        if (store.skipProcessConfirmationModalEnabled && store.loadedCohortId) {
          writeCohortProcessPendingMarker(store.loadedCohortId);
        }
        store.setExecutionPending(true);
        store.setProfileStatus(CohortDefinitionStatus.PROCESSING);
        // The run commits any staged draft server-side, so clear the now-stale
        // local staged-changes flag (pill drops; PROCESSING gates re-staging).
        store.setHasStagedChanges(false);
      },
    }),
    [queryClient]
  );
};

/**
 * Shared chat-page mount for the agent-first project layout (OR-5963 step 6).
 *
 * Before step 6 only the left pane subscribed to the active chat: the
 * chat panel called ``useEpiChatPage`` directly and the right pane was
 * "just a route". Step 6 introduces the draft surface
 * (``ProjectVariableDraftPage``), which needs to consume the SAME chat
 * stream — specifically ``page.artifacts`` and ``page.remoteFocusScope``
 * — to render an in-flight AV draft on the right side while the user
 * keeps chatting on the left.
 *
 * ``useEpiChatPage`` owns per-session reducer state in local React
 * ``useState`` and opens its own socket subscribe. Calling it twice for
 * the same ``chatId`` would (a) duplicate the in-memory state slot and
 * (b) double-subscribe on the socket. So we lift the call into this
 * provider once and fan it out to both panes via context.
 *
 * The provider is only mounted when ``chatId`` is set — without an
 * active chat there is no ``useEpiChatPage`` instance and the context
 * value is ``null``. Consumers must handle that case (and typically do
 * by short-circuiting to an empty state, e.g. the draft page renders
 * "open a chat to see drafts" copy).
 */
export interface ActiveChatContextValue {
  /** The chat ``page`` bundle — messages, artifacts, scope, run state. */
  page: UseEpiChatPageResult;
  /** Active chat session id (matches ``?chat=<id>``). */
  chatId: string;
  /** Composer chip + placeholder bound to the URL-derived scope. */
  scopeChip: ChatComposerScopeChip | null;
  composerPlaceholder: string | undefined;
}

const ActiveChatContext = createContext<ActiveChatContextValue | null>(null);

interface ActiveChatProviderProps {
  projectId: string;
  chatId: string;
  children: ReactNode;
}

export const ActiveChatProvider = ({ projectId, chatId, children }: ActiveChatProviderProps) => {
  const { scope, scopeChip, composerPlaceholder } = useChatScopeBindings(projectId);
  const wireScope = useMemo(() => toWireScope(scope), [scope]);
  const resourceResolvers = useProjectPageResourceResolvers();
  const page = useEpiChatPage({
    wireScope,
    chatId,
    resourceResolvers,
  });

  const value = useMemo<ActiveChatContextValue>(
    () => ({ page, chatId, scopeChip, composerPlaceholder }),
    [page, chatId, scopeChip, composerPlaceholder]
  );

  return <ActiveChatContext.Provider value={value}>{children}</ActiveChatContext.Provider>;
};

/**
 * Read the active chat context. Returns ``null`` when no chat is open
 * (``?chat=`` absent) — callers must handle the empty case.
 */
export const useActiveChatContext = (): ActiveChatContextValue | null => {
  return useContext(ActiveChatContext);
};
